import { Component, OnInit, OnDestroy, ViewChild, ViewContainerRef, ComponentRef } from '@angular/core';
import { NotificationsFacade } from '../../+state/notifications.facade';
import { map, delay, tap, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { groupBy } from 'lodash';
import { FileNotificationType } from '../../models/file-notification-type.enum';
import { NotificationToastComponent } from '../../components/notification-toast/notification-toast.component';
import { ToastsNotificationService } from '../../services/toast-notifications.service';
import { NotificationType } from '../../models/notification-type.enum';

@Component({
  selector: 'vpfa-notifications-container',
  templateUrl: './notifications-container.component.html',
  styleUrls: ['./notifications-container.component.scss'],
})
export class NotificationsContainerComponent implements OnInit, OnDestroy {
  @ViewChild('toastsContainerRef', { read: ViewContainerRef }) toastsContainerRef?: ViewContainerRef;

  // show notification for files that was checked by FE and are invalid (before ConfirmBroadcastImages)
  openModalIfInvalidFiles$ = combineLatest([
    this.notificationsFacade.filesNotifications$,
    this.notificationsFacade.invalidFilesCount$,
  ]).pipe(
    map(([notifications, invalidFilesCount]) => ({
      notificationsCount: notifications?.filter(x => x.notificationType === FileNotificationType.NotAccepted).length,
      invalidFilesCount,
    })),
    map(
      ({ notificationsCount, invalidFilesCount }) => notificationsCount > 0 && notificationsCount === invalidFilesCount,
    ),
  );

  // show notification for files that are uploaded to BE (after ConfirmBroadcastImages)
  openModalAfterAllFilesUpload$ = combineLatest([
    this.notificationsFacade.filesNotifications$,
    this.notificationsFacade.filesUploadCount$,
  ]).pipe(
    map(
      ([currentNotifications, uploadFilesCount]) =>
        currentNotifications?.length > 0 && uploadFilesCount === currentNotifications.length,
    ),
  );

  modalOpen$ = combineLatest([
    this.notificationsFacade.basicNotifications$.pipe(map(items => items?.length > 0)),
    this.notificationsFacade.filesNotifications$.pipe(map(items => items?.length > 0)),
    this.openModalIfInvalidFiles$,
    this.openModalAfterAllFilesUpload$,
  ]).pipe(
    map(x => x.some(x => x === true)),
    tap(isOpened => {
      if (isOpened) {
        this.focusCloseButton();
      }
    }),
  );

  modalMaskClosable = true;

  /**
   * To indicate if modal is currently closing. This variable is used to fix NzModal bug, preconditions:
   * 1. Modal was opened and is currently closing (animation is playing);
   * 2. Event/action is sent to modal to open it again
   *
   * When above happens, it is likely to modal be still closed but modal overlay to be visible and notifications
   * that meant to be displayed are lost.
   */
  isClosing$ = new BehaviorSubject(false);

  errorNotifications$ = this.notificationsFacade.errorNotifications$.pipe(map(list => groupBy(list, 'title')));
  warningNotifications$ = this.notificationsFacade.warningNotifications$.pipe(map(list => groupBy(list, 'title')));
  successNotifications$ = this.notificationsFacade.successNotifications$.pipe(map(list => groupBy(list, 'title')));
  mistakeNotifications$ = this.notificationsFacade.mistakeNotifications$.pipe(map(list => groupBy(list, 'title')));

  filesErrorsList$ = this.notificationsFacade.filesNotifications$.pipe(
    map(x => x.filter(x => x.notificationType === FileNotificationType.NotAccepted)),
  );
  filesSuccessList$ = this.notificationsFacade.filesNotifications$.pipe(
    map(x => x.filter(x => x.notificationType === FileNotificationType.Accepted)),
  );

  filesUploadCount$ = this.notificationsFacade.filesUploadCount$;
  invalidFilesCount$ = this.notificationsFacade.invalidFilesCount$;

  private _onDestroy$ = new Subject<void>();

  constructor(
    private notificationsFacade: NotificationsFacade,
    private toastsNotificationService: ToastsNotificationService,
  ) {}

  ngOnInit() {
    this.blockClosingOfModalWhenNewNotificationIsAdded();
    this.toastsNotificationService.createToastHandler = (
      title: string,
      content: string,
      type: NotificationType,
      interpolateParams?: Object,
    ) => this.createToast(title, content, type, interpolateParams);
  }

  onClose() {
    this.isClosing$.next(true);
    this.notificationsFacade.clearAll();
  }

  afterClose() {
    this.isClosing$.next(false);
  }

  focusCloseButton() {
    setTimeout(() => {
      document.querySelector<HTMLButtonElement>('#closeNotificationBtn')?.focus();
    }, 100);
  }

  private blockClosingOfModalWhenNewNotificationIsAdded() {
    this.notificationsFacade.basicNotifications$
      .pipe(
        tap(() => {
          this.modalMaskClosable = false;
        }),
        delay(600),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => {
        this.modalMaskClosable = true;
      });
  }

  private createToast(title: string, content: string, type: NotificationType, interpolateParams?: Object) {
    const toast = this.toastsContainerRef.createComponent(NotificationToastComponent);

    toast.instance.title = title;
    toast.instance.content = content;
    toast.instance.interpolateParams = interpolateParams;


    return toast;
  }

  ngOnDestroy() {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
